import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import productService from '../../services/productService';
import './ProductDetailPage.css';

function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productService.getProduct(id);
        const productData = response.data;

        // Map backend sortOrder to frontend displayOrder
        if (productData.images && Array.isArray(productData.images)) {
          productData.images = productData.images.map(img => ({
            ...img,
            displayOrder: img.sortOrder  // Map sortOrder → displayOrder
          }));
        }

        setProduct(productData);
      } catch (err) {
        setError('Failed to load product');
        console.error('Error fetching product:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }

    try {
      await productService.deleteProduct(id);
      toast.success('Product deleted successfully');
      navigate('/products');
    } catch (err) {
      toast.error('Failed to delete product');
      console.error('Error deleting product:', err);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', className: 'badge-success' },
      draft: { label: 'Draft', className: 'badge-warning' },
      archived: { label: 'Archived', className: 'badge-secondary' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  if (isLoading) {
    return (
      <div className="product-detail-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading product...</span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="page-error">
          <h2>Product Not Found</h2>
          <p>{error || 'The product you are looking for does not exist.'}</p>
          <Link to="/products" className="btn btn-primary">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="header-title-row">
            <h1>{product.name}</h1>
            {getStatusBadge(product.status)}
          </div>
          <p>SKU: {product.sku}</p>
        </div>
        <div className="page-header-actions">
          <Link to={`/products/${id}/edit`} className="btn btn-secondary">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit
          </Link>
          <button className="btn btn-danger" onClick={handleDelete}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            Delete
          </button>
        </div>
      </div>

      <div className="detail-grid">
        {/* Main Content */}
        <div className="detail-main">
          <div className="detail-section">
            <h3>Product Images</h3>
            <div className="product-images">
              {product.images && product.images.length > 0 ? (
                <div className="image-gallery">
                  {product.images.map((image) => (
                    <div key={image.id} className={`image-item ${image.isPrimary ? 'primary' : ''}`}>
                      <img src={image.url} alt={image.alt || product.name} />
                      {image.isPrimary && <span className="primary-badge">Primary</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-images">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  <p>No images uploaded</p>
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>Description</h3>
            <p className="description-text">{product.description}</p>
            {product.shortDescription && (
              <p className="short-description">
                <strong>Short description:</strong> {product.shortDescription}
              </p>
            )}
          </div>

          <div className="detail-section">
            <h3>Product Details</h3>
            <div className="details-grid">
              {product.artist && (
                <div className="detail-item">
                  <span className="detail-label">Artist</span>
                  <span className="detail-value">{product.artist}</span>
                </div>
              )}
              {product.dimensions && (
                <div className="detail-item">
                  <span className="detail-label">Dimensions</span>
                  <span className="detail-value">{product.dimensions}</span>
                </div>
              )}
              {product.materials && (
                <div className="detail-item">
                  <span className="detail-label">Materials</span>
                  <span className="detail-value">{product.materials}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Category</span>
                <span className="detail-value">{product.category?.name || '-'}</span>
              </div>
              {product.subcategory && (
                <div className="detail-item">
                  <span className="detail-label">Subcategory</span>
                  <span className="detail-value">{typeof product.subcategory === 'object' ? product.subcategory?.name : product.subcategory}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          <div className="detail-section">
            <h3>Pricing</h3>
            <div className="price-display">
              <span className="current-price">{formatPrice(product.price)}</span>
              {product.compareAtPrice && (
                <span className="compare-price">{formatPrice(product.compareAtPrice)}</span>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>Inventory</h3>
            <div className="inventory-info">
              <div className="inventory-row">
                <span className="label">Stock</span>
                <span className={`value ${product.stock <= product.lowStockThreshold ? 'low-stock' : ''}`}>
                  {product.stock} units
                  {product.stock <= product.lowStockThreshold && product.status === 'active' && (
                    <span className="low-stock-badge">Low</span>
                  )}
                </span>
              </div>
              <div className="inventory-row">
                <span className="label">Low stock threshold</span>
                <span className="value">{product.lowStockThreshold} units</span>
              </div>
              {product.isLimitedEdition && (
                <>
                  <div className="inventory-row">
                    <span className="label">Limited Edition</span>
                    <span className="value">Yes</span>
                  </div>
                  {product.editionSize && (
                    <div className="inventory-row">
                      <span className="label">Edition Size</span>
                      <span className="value">{product.editionSize}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>Accessibility</h3>
            <div className="accessibility-info">
              <div className="accessibility-item">
                <span className={`status-dot ${product.hasAudioDescription ? 'active' : ''}`}></span>
                <span>Audio description</span>
              </div>
              <div className="accessibility-item">
                <span className={`status-dot ${product.hasVideoContent ? 'active' : ''}`}></span>
                <span>Video content</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Timestamps</h3>
            <div className="timestamps">
              <div className="timestamp-row">
                <span className="label">Created</span>
                <span className="value">{formatDate(product.createdAt)}</span>
              </div>
              <div className="timestamp-row">
                <span className="label">Last updated</span>
                <span className="value">{formatDate(product.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailPage;
